import { ComponentProps, useMemo } from 'react';
import { RSCImage } from 'react-datocms';
import { CdaStructuredTextRecord } from 'react-datocms/structured-text';
import clsx from 'clsx';
import CtaButton from 'components/CtaButton';
import useConfig from 'services/config';
import { Image as ImageType, ResponsiveImage } from 'services/graphql';

import ComparisonEntry from './ComparisonEntry';

import styles from './ComparisonBlock.module.css';

interface ComparisonBlockRecord extends CdaStructuredTextRecord {
  ourLogo?: ResponsiveImage | ImageType | null;
  ourLabel: string;
  competitorsLabel?: string;
  cta?: string;
  ctaLabel?: string;
  items: ComponentProps<typeof ComparisonEntry>[];
}

export default function ComparisonBlock({
  ourLogo,
  ourLabel,
  competitorsLabel,
  cta,
  ctaLabel,
  items,
}: ComparisonBlockRecord) {
  const config = useConfig();
  const logoData = useMemo(() => {
    const lightLogo = config.header?.lightLogo || config.footer?.logo || config.header?.logo;
    const fallback = lightLogo ? { ...lightLogo, src: lightLogo.url } : undefined;
    if (!ourLogo) return fallback;

    const img = 'responsiveImage' in ourLogo ? ourLogo.responsiveImage : ourLogo;
    if (!img) return fallback;

    return img.src ? img : { ...img, src: (img as ImageType).url };
  }, [ourLogo, config]);

  return (
    <div className={clsx(styles.container)}>
      <div className={clsx(styles.body)}>
        <div className={styles.headerRow}>
          <h3 className={clsx(styles.header, styles.our)}>
            {logoData && <RSCImage data={logoData} />}
            {ourLabel}
          </h3>
          <div />
          <h3 className={clsx(styles.header, styles.competitor)}>{competitorsLabel}</h3>
        </div>
        {items?.map(ComparisonEntry)}
      </div>
      {cta && (
        <div className={styles.footer}>
          {ctaLabel && <p>{ctaLabel}</p>}
          <CtaButton value={cta} />
        </div>
      )}
    </div>
  );
}
